import { useMutation, useQuery } from '@apollo/client';
import { useHubble } from '@seek/hubble';
import { useMelwaysInfo, useMelwaysLink } from '@seek/melways-react';
import { metrics } from '@seek/metrics-js';
import { useEffect, useMemo, useRef } from 'react';
import { v4 as uuid } from 'uuid';

import { TRACK_JOB_APPLICATION_STARTED } from 'src/components/App/application-started/track-job-application-started.graphql';
import { Page, useTrackingClient } from 'src/context/tracking';
import { useTestRecord } from 'src/context/use-test-record';
import { GET_JOB_APPLICATION_PROCESS } from 'src/context/useJobDetails/graphql/jobs.graphql';
import { usePersonalDetails } from 'src/context/usePersonalDetails';
import { logError, logInfo } from 'src/modules/error-logger/browserLog';
import { GET_USER } from 'src/modules/graphql-client/queries/user.graphql';
import { GraphQLErrorCode } from 'src/modules/graphql-client/types';
import { getJobSolMetadata } from 'src/modules/solTracker/solMetadata';
import { isSeekTest } from 'src/modules/testUtils';
import type { Config } from 'src/types';
import { didResolverErrorOccur, getSessionId, navigateToUrl } from 'src/utils';

interface Arguments {
  jobId: string;
  config: Config;
  applicationCorrelationId: string;
  isNewApplication: boolean;
}

export const useInitApp = ({
  jobId,
  config,
  applicationCorrelationId: appId,
  isNewApplication,
}: Arguments) => {
  const hubble = useHubble();
  const { testRecord } = useTestRecord();
  const { language } = useMelwaysInfo();
  const user = useQuery(GET_USER, {
    errorPolicy: 'all',
    fetchPolicy: 'network-only', // don't wanna rerender when cache is updated
  });
  const isAuth = Boolean(user.data?.viewer?._id);
  const { data } = usePersonalDetails();
  const verifiedIdentityName = data?.viewer?.verifiedIdentity?.name;
  const jdvUrl = useMelwaysLink(config.MISSING_JOB + jobId);

  const applicationProcess = useQuery(GET_JOB_APPLICATION_PROCESS, {
    variables: {
      jobId,
      isAuthenticated: isAuth,
      locale: config.VOCAB_LANGUAGE,
    },
    errorPolicy: 'all',
    fetchPolicy: 'network-only', // don't wanna rerender when cache is updated
    onError(error) {
      if (
        didResolverErrorOccur(
          ['jobApplicationProcess', 'questionnaire'],
          error.graphQLErrors,
        )
      ) {
        logError(
          {
            ...error,
            type: 'questionnaire-fetch-failed',
            message: 'Failed to resolve Questionnaire',
          },
          { jobId, applicationCorrelationId: appId, error },
        );
      }
    },
  });

  const { jobApplicationProcess } = applicationProcess.data || {};
  const isLinkOut = jobApplicationProcess?.linkOut ?? false;

  const loading = user.loading || applicationProcess.loading;
  const userErrors = user.error?.graphQLErrors.filter(
    (err) => err.extensions?.code !== GraphQLErrorCode.unauthenticated,
  );
  const error = applicationProcess.error || (userErrors?.length && user.error);
  const [trackApplicationStarted] = useMutation(TRACK_JOB_APPLICATION_STARTED);

  const trackingProps = useMemo(() => {
    const jobDetails = jobApplicationProcess?.job;

    return {
      isLoggedIn: Boolean(user.data?.viewer?._id),
      loginId: user.data?.viewer?._id || null,
      profileGuid: user.data?.viewer?.trackingId || null,
      advertiserId: jobDetails?.advertiser?.id || '',
      advertiserName: jobDetails?.advertiser?.name || '',
      jobId: jobDetails?.id || '',
      jobLocation: jobApplicationProcess?.location?.name,
      jobLocationId: jobApplicationProcess?.location?.id,
      jobArea: jobApplicationProcess?.area?.name,
      jobAreaId: jobApplicationProcess?.area?.id,
      jobClassification: jobApplicationProcess?.classification?.name,
      jobClassificationId: jobApplicationProcess?.classification?.id,
      jobSubClassification:
        jobApplicationProcess?.classification?.subClassification?.name,
      jobSubClassificationId:
        jobApplicationProcess?.classification?.subClassification?.id,
      jobHasRoleRequirements:
        jobDetails?.tracking?.hasRoleRequirements || false,
      jobHasScreen: false,
      jobIsLinkOut: Boolean(jobApplicationProcess?.linkOut),
      isProfileApply: true,
      jobListingSource: jobDetails?.source,
      isCandidateIdentityVerified: Boolean(verifiedIdentityName),
      brand: config.BRAND,
      siteCountry: config.LOCALE,
      siteLanguage: language,
      zone: config.ZONE,
    };
  }, [jobApplicationProcess, user, config, language, verifiedIdentityName]);

  const trackingClient = useTrackingClient(trackingProps);
  const trackingCorrelationIdLoad = useRef(uuid());
  const trackingCorrelationIdStart = useRef(uuid());

  // Redirect to JDV: Missing job / HK Private Advertiser
  useEffect(() => {
    const apLoading = applicationProcess.loading;
    const job = applicationProcess.data?.jobApplicationProcess?.job;
    const isMissingJob = !apLoading && !job;
    const isHKPrivateAdvertiser =
      !apLoading &&
      job?.tracking?.isPrivateAdvertiser &&
      config.LOCALE === 'HK';

    if (isMissingJob) {
      logError(
        {
          type: 'job-not-found',
          message: `Failed to resolve job ${jobId}, job missing`,
        },
        { jobId },
      );
    }

    if (isMissingJob || isHKPrivateAdvertiser) {
      return navigateToUrl(jdvUrl, Boolean(isHKPrivateAdvertiser));
    }
  }, [applicationProcess, config, jobId, jdvUrl]);

  useEffect(() => {
    if (!loading && !isLinkOut && isAuth) {
      trackingClient.trackView({
        view: Page.DOCUMENT_PAGE,
        eventName: ['apply_load_succeeded'],
        trackingCorrelationId: trackingCorrelationIdLoad.current,
      });

      hubble.trackEvent(
        'job_apply_displayed',
        {
          jobId,
          solMetadata: getJobSolMetadata(jobId),
          applicationCorrelationId: appId,
        },
        {
          trackingCorrelationId: trackingCorrelationIdLoad.current,
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isAuth, isLinkOut]);

  useEffect(() => {
    metrics.count('Seek-Test-Hubble', [`seek-test:${isSeekTest()}`]);
  }, [testRecord]);

  useEffect(() => {
    if (loading) return;

    const sendApplicationStartedEvents = async () => {
      metrics.sendInitialLoadTime('job-apply-frontend');
      trackingClient.trackView({
        view: Page.DOCUMENT_PAGE,
        eventName: ['apply_start_succeeded'],
        trackingCorrelationId: trackingCorrelationIdStart.current,
      });

      hubble.trackEvent(
        'job_apply_started',
        {
          jobId,
          thirdPartyLink: false,
          applicationCorrelationId: appId,
        },
        {
          trackingCorrelationId: trackingCorrelationIdStart.current,
        },
      );

      await trackApplicationStarted({
        variables: {
          input: {
            jobId,
            sessionId: getSessionId(),
            applicationCorrelationId: appId,
            isProfileApply: true,
          },
        },
      });

      logInfo(`Application started for JobId ${jobId}`, 'application-started', {
        jobId,
        applicationCorrelationId: appId,
      });
    };

    if (isNewApplication && !isLinkOut) {
      sendApplicationStartedEvents();
    }

    // only run on mount once when loading is complete
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  return {
    loading: !error && loading,
    error: !isLinkOut && error,
    isAuth,
    isLinkOut,
  };
};
